import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Stack,
  IconButton,
} from '@mui/material';
import { TransportMethod } from '@domain/shipment/models/TransportMethod';
import { useShipmentMethods } from '../../hooks/useShipmentMethods';
import DialogHeader from '@shared/ui/dialog/DialogHeader';
import { Close } from '@mui/icons-material';
import { TransportMethodIcon } from '@features/shipments/components/shared/TransportMethodIcon';

interface CreateShipmentMethodDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (shipmentMethod: any) => void;
}

const CreateShipmentMethodDialog: React.FC<CreateShipmentMethodDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    transportMethod: TransportMethod.BOAT,
    duration: 0,
    name: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { createShipmentMethod, isLoading } = useShipmentMethods();

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.duration) {
      newErrors.duration = 'Duration is required';
    }

    if (formData.duration < 0) {
      newErrors.duration = 'Duration must be a positive number';
    }

    if (formData.duration > 365) {
      newErrors.duration = 'Duration cannot exceed 365 days';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      const result = await createShipmentMethod(formData);
      if (result) {
        onSuccess?.(result);
        handleClose();
      }
    } catch (error) {
      console.error('Failed to create shipment method:', error);
    }
  };

  const handleClose = () => {
    setFormData({
      transportMethod: TransportMethod.TRUCK,
      duration: 0,
      name: '',
    });
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogHeader>
        <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
          <Typography variant="h6">   Create Shipment Method</Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Stack>
      </DialogHeader>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
          <TextField
            label="Name (Optional)"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            fullWidth
            placeholder="e.g., Express Truck Delivery"
          />

          <FormControl fullWidth>
            <InputLabel>Transport Method</InputLabel>
            <Select
              value={formData.transportMethod}
              onChange={(e) => handleChange('transportMethod', e.target.value)}
              label="Transport Method"
            >
              {Object.values(TransportMethod).map((method) => (
                <MenuItem key={method} value={method}>

                  <Stack direction="row" alignItems="center" gap={2}>
                    <TransportMethodIcon size={24} transportMethod={method} />
                    {method.charAt(0).toUpperCase() + method.slice(1)}
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Duration (Days)"
            type="number"
            value={formData.duration}
            onChange={(e) => handleChange('duration', parseInt(e.target.value) || undefined)}
            fullWidth
            error={!!errors.duration}
            helperText={errors.duration}
            inputProps={{ min: 0, max: 365 }}
          />

        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading}
        >
          {isLoading ? 'Creating...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateShipmentMethodDialog;
